import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import ApiService from "../../services/ApiService";
import Spinner from "@cs/components/src/Spinner";
import Banner from "@cs/components/src/Banner";
import Section from "@cs/components/src/Section";
import { useOrganizationQuery } from "../../hooks/useOrganizationQuery";
import { css, styled } from "styled-components";
import { useScriptExecutionValues } from "./jobUtil";
import Button from "@cs/components/src/Button";
import { Link, useParams } from "react-router-dom";
import { Play, XCircle } from "react-feather";
import { developerToolsEnabled } from "../../services/env";

interface Props {
  instanceId?: string;
  systemDomainId?: string;
  id?: string;
  modal?: boolean;
  onClose?: () => void;
}

const Entry = styled.div`
  display: grid;
  grid-template-columns: 25% 1fr;
  padding-bottom: 15px;
  color: var(--gray300);
  font-size: 14px;
`;
const TitleLink = styled(Link)`
  color: var(--blue400);

  &:hover {
    text-decoration: underline;
  }
`;

const StyledValue = styled.div<{ $multiline?: boolean }>`
  ${(props) =>
    props.$multiline &&
    css`
      background-color: var(--dark300);
      padding: 10px;
      border-radius: 5px;
      line-height: 20px;
      white-space: pre-wrap;
      word-wrap: break-word;
      overflow: hidden;
      min-height: 30px;
      font-family: "Source Code Pro", monospace;
      font-size: 13px;
      margin-top: -5px;
      margin-bottom: 5px;
      max-height: 30vh;
      overflow: auto;
    `}
`;

const JobView = (props: Props) => {
  const { modal, systemDomainId, instanceId, id, onClose } = props;
  const path = `/api/script_execution/${systemDomainId}/${instanceId}/${id}`;
  const [refetch, setRefetch] = useState(false);
  const {
    data: script,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["job", systemDomainId, instanceId, id],
    queryFn: async () => {
      const response = await ApiService.fetch<{ data: ScriptExecution }>(
        "GET",
        path
      );
      return response.data;
    },
    refetchInterval: refetch ? 5000 : undefined,
    onSuccess: (data) => {
      setRefetch(
        data.scriptExecution.status === "running" ||
          data.scriptExecution.status === "waiting" ||
          data.scriptExecution.status === "retrying"
      );
    },
    enabled: !!(id && systemDomainId && instanceId),
  });
  const { data: organizations } = useOrganizationQuery();
  const queryClient = useQueryClient();
  const values = useScriptExecutionValues(script, organizations);
  if (values && script?.scriptExecution.pluginInstanceId) {
    values.title = {
      ...values.title,
      value: (
        <TitleLink
          to={`/dashboard/${systemDomainId}/plugin/${script.scriptExecution.pluginInstanceId}`}
        >
          {values.title.value}
        </TitleLink>
      ),
    };
  }

  const localRunMutation = useMutation({
    mutationFn: async () => {
      await ApiService.fetch("POST", `${path}/message`, {
        message: "[UI] Starting local run",
        abort: false,
        progressNumerator: 0,
      });

      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ["job", systemDomainId, instanceId, id],
        });
      }, 1000);

      try {
        // No Authorization header to forward anymore — the session lives in an httpOnly
        // cookie. It still reaches this request because the cookie is host-only, not
        // port-scoped: as long as API_HOST is the same host the cookie was set for
        // (the common case — a local backend on a different port), the browser attaches
        // it automatically via credentials: "include".
        const response = await fetch(
          `http://${
            localStorage.getItem("API_HOST") || "localhost:4000"
          }/api/script_execution/run/local/${systemDomainId}/${instanceId}/${id}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            credentials: "include",
          }
        );
        if (!response.ok) {
          throw new Error(response.statusText);
        }
      } catch (e) {
        const err = e as Error;
        await ApiService.fetch("POST", `${path}/message`, {
          message: "[UI] Local run failed: " + err.message,
          failed: true,
        });
      }
    },
  });

  const retryMutation = useMutation({
    mutationFn: async () => {
      await ApiService.fetch(
        "POST",
        `/api/script_execution/retry/${systemDomainId}/${instanceId}/${id}`
      );
      queryClient.invalidateQueries({
        queryKey: ["job", systemDomainId, instanceId, id],
      });
      queryClient.invalidateQueries({ queryKey: ["jobList"] });
    },
  });

  const abortMutation = useMutation({
    mutationFn: async () => {
      await ApiService.fetch("POST", `${path}/message`, {
        message: "[UI] User requested abort",
        abort: true,
      });
      queryClient.invalidateQueries({
        queryKey: ["job", systemDomainId, instanceId, id],
      });
    },
  });

  const resolveErrorMutation = useMutation({
    mutationFn: async () => {
      await ApiService.fetch(
        "POST",
        `/api/script_execution/error/resolve/${systemDomainId}/${instanceId}`
      );
      queryClient.invalidateQueries({ queryKey: ["jobList"] });
      onClose?.();
    },
  });

  const clearErrorsMutation = useMutation({
    mutationFn: async () => {
      await ApiService.fetch(
        "POST",
        `/api/script_execution/error/clear/${systemDomainId}/${instanceId}`
      );
      queryClient.invalidateQueries({ queryKey: ["jobList"] });
      onClose?.();
    },
  });

  return isLoading ? (
    <Spinner />
  ) : isError ? (
    <Banner error="Error loading jobs" />
  ) : script ? (
    <Section
      title="Job Details"
      pad
      modal={modal}
      onClose={onClose}
      buttons={
        <>
          {refetch && <Spinner />}
          {localRunMutation.error && (
            <Banner error="Failed to start local run" />
          )}
          {retryMutation.error && <Banner error="Failed to retry job" />}
          {abortMutation.error && <Banner error="Failed to abort job" />}
          {resolveErrorMutation.error && (
            <Banner error="Failed to redrive plugin" />
          )}
          {clearErrorsMutation.error && (
            <Banner error="Failed to clear errors" />
          )}
          {developerToolsEnabled && (
            <Button
              label="Local Run"
              borderless
              type="default"
              onClick={() => localRunMutation.mutate()}
              loading={localRunMutation.isLoading}
            />
          )}
          {script.scriptExecution.status === "error" &&
            script.scriptExecution.pluginInstanceId && (
              <>
                <Button
                  label="Clear Errors"
                  type="danger"
                  icon={XCircle}
                  onClick={() => clearErrorsMutation.mutate()}
                  loading={clearErrorsMutation.isLoading}
                />
                <Button
                  label="Redrive Plugin"
                  type="success"
                  icon={Play}
                  onClick={() => resolveErrorMutation.mutate()}
                  loading={resolveErrorMutation.isLoading}
                />
              </>
            )}
          <Button
            label="Retry"
            type="warning"
            icon={Play}
            onClick={() => retryMutation.mutate()}
            loading={retryMutation.isLoading}
          />
          {(script.scriptExecution.status === "running" ||
            script.scriptExecution.status === "waiting" ||
            script.scriptExecution.status === "retrying") && (
            <Button
              label="Abort"
              type="danger"
              onClick={() => abortMutation.mutate()}
              loading={abortMutation.isLoading}
            />
          )}
        </>
      }
    >
      {Object.values(values ?? {})
        .filter(({ value }) => value)
        .map(({ label, value, multiline }) => (
          <Entry key={label}>
            <div>{label}:</div>
            <StyledValue $multiline={multiline}>{value}</StyledValue>
          </Entry>
        ))}
    </Section>
  ) : (
    <></>
  );
};

export const JobViewParams = () => {
  const { systemDomainId, instanceId, jobId } = useParams();

  return (
    <JobView
      systemDomainId={systemDomainId}
      instanceId={instanceId}
      id={jobId}
    />
  );
};

export default JobView;
